import type { FC } from "react";
import { NextSeo } from "next-seo";
import { useRouter } from "next/router";

import { config } from "@/config";
import {
  DEFAULT_DESCRIPTION,
  DEFAULT_OG_IMAGE,
  DEFAULT_TITLE,
  type OpenGraphImage,
} from "@/components/PageMetaManager/config";

const SITE_NAME = "Matic Robots";

function removeTrailingSlash(str: string): string {
  return str.endsWith("/") ? str.slice(0, -1) : str;
}

interface PageConfig {
  title?: string;
  description?: string;
  canonical?: string;
  noindex?: boolean;
  nofollow?: boolean;
  openGraph?: {
    url?: string;
    site_name?: string;
    title?: string;
    description?: string;
    images?: Array<{
      type?: string;
      width?: number;
      height?: number;
      url: string;
    }>;
  };
  twitter?: {
    title?: string;
    description?: string;
    cardType?: string;
  };
}

interface MaticPageMetaProps {
  page?: string;
  config?: Record<string, PageConfig> | unknown;
  overrideTitle?: string;
  overrideDescription?: string;
  overrideImage?: OpenGraphImage;
}

const MaticPageMeta: FC<MaticPageMetaProps> = (props) => {
  const router = useRouter();
  const pageKey = props.page || "_default";
  const pageConfigs = (props.config || {}) as Record<string, PageConfig>;
  // Nested copies: the assignments below must not mutate the shared config entry.
  const pageConfig: PageConfig = {
    ...pageConfigs[pageKey],
    openGraph: { ...pageConfigs[pageKey]?.openGraph },
    twitter: { ...pageConfigs[pageKey]?.twitter },
  };

  pageConfig.title = props.overrideTitle || pageConfig.title || DEFAULT_TITLE;
  pageConfig.description =
    props.overrideDescription || pageConfig.description || DEFAULT_DESCRIPTION;
  pageConfig.canonical = removeTrailingSlash(
    pageConfig.canonical ||
      `https://${config.web.domain}${(router.asPath || "").split(/[?#]/)[0]}`,
  );
  pageConfig.openGraph = pageConfig.openGraph || {};
  pageConfig.twitter = pageConfig.twitter || {};

  if (
    props.page === "404" ||
    props.page === "orderConfirm" ||
    props.page === "support"
  ) {
    pageConfig.noindex = true;
    pageConfig.nofollow = true;
  }

  /* Open Graph */
  pageConfig.openGraph.url =
    pageConfig.canonical || `${config.web.domain}${router.asPath}`;
  pageConfig.openGraph.site_name = pageConfig.openGraph.site_name || SITE_NAME;
  pageConfig.openGraph.title =
    pageConfig.openGraph.title || pageConfig.title || DEFAULT_TITLE;
  pageConfig.openGraph.description =
    pageConfig.openGraph.description ||
    pageConfig.description ||
    DEFAULT_DESCRIPTION;
  pageConfig.openGraph.images = props.overrideImage
    ? [props.overrideImage]
    : pageConfig.openGraph.images || [DEFAULT_OG_IMAGE];

  /* Twitter */
  pageConfig.twitter.title =
    pageConfig.twitter.title || pageConfig.title || DEFAULT_TITLE;
  pageConfig.twitter.description =
    pageConfig.twitter.description ||
    pageConfig.description ||
    DEFAULT_DESCRIPTION;
  pageConfig.twitter.cardType =
    pageConfig.twitter.cardType || "summary_large_image";

  return <NextSeo {...pageConfig} />;
};

export default MaticPageMeta;
